import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polygon } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    FaArrowLeft,
    FaSatellite,
    FaCalendarAlt,
    FaMapMarkedAlt,
    FaLayerGroup,
    FaCopy,
    FaChevronDown,
    FaChevronUp,
    FaCloud,
    FaChartLine,
    FaThLarge,
    FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    Legend,
} from "recharts";

import Breadcrumbs from "../../components/BreadCrumbs";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import { getPolygon } from '../../services/dynacrop';
import { formatDate } from "../../utils/FormatDateTime";

/* -------------------- HELPERS -------------------- */

const parseWKTPolygon = (wkt) => {
    if (!wkt) return [];
    try {
        return wkt
            .replace("POLYGON ((", "")
            .replace("))", "")
            .split(",")
            .map((point) =>
                point
                    .trim()
                    .split(" ")
                    .reverse()
                    .map(Number)
            );
    } catch {
        return [];
    }
};

const getStatusColor = (status) => {
    switch (status) {
        case "completed":
            return "bg-green-100 text-green-700 border-green-200";
        case "pending":
            return "bg-yellow-100 text-yellow-700 border-yellow-200";
        case "failed":
            return "bg-red-100 text-red-700 border-red-200";
        default:
            return "bg-gray-100 text-gray-700 border-gray-200";
    }
};

const getStatusDotColor = (status) => {
    switch (status) {
        case "completed":
            return "bg-green-500";
        case "pending":
            return "bg-yellow-500";
        case "failed":
            return "bg-red-500";
        default:
            return "bg-gray-500";
    }
};

// Group observations by year
const groupObservationsByYear = (observations) => {
    const grouped = {};
    observations?.forEach((date) => {
        const year = date.substring(0, 4);
        if (!grouped[year]) grouped[year] = 0;
        grouped[year]++;
    });
    return Object.entries(grouped).map(([year, count]) => ({ year, count }));
};

// Process cloud cover data for chart
const processCloudCoverData = (cloudCover) => {
    if (!cloudCover) return [];

    // Group by month and calculate average
    const monthlyData = {};
    cloudCover.forEach(([date, cover]) => {
        const month = date.substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
            monthlyData[month] = { sum: 0, count: 0 };
        }
        monthlyData[month].sum += cover * 100;
        monthlyData[month].count++;
    });

    return Object.entries(monthlyData)
        .map(([month, { sum, count }]) => ({
            month,
            cloudCover: Math.round(sum / count),
        }))
        .slice(-24); // Last 24 months
};

// Calculate cloud statistics
const calculateCloudStats = (cloudCover) => {
    if (!cloudCover || cloudCover.length === 0) return { avg: 0, clear: 0, cloudy: 0 };

    const total = cloudCover.length;
    const clearDays = cloudCover.filter(([_, cover]) => cover <= 0.2).length;
    const cloudyDays = cloudCover.filter(([_, cover]) => cover > 0.5).length;
    const avgCover = cloudCover.reduce((sum, [_, cover]) => sum + cover, 0) / total;

    return {
        avg: Math.round(avgCover * 100),
        clear: Math.round((clearDays / total) * 100),
        cloudy: Math.round((cloudyDays / total) * 100),
    };
};

/* -------------------- UI COMPONENTS -------------------- */

const InsightCard = ({ label, value, icon: Icon, color = "primary", subtext }) => (
    <div className="bg-white border rounded-xl p-5 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
            <div className="flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
                {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
            </div>
            {Icon && (
                <div className={`p-3 rounded-lg bg-${color}/10`}>
                    <Icon className={`text-${color} text-lg`} />
                </div>
            )}
        </div>
    </div>
);

const KeyValue = ({ label, value, copyable = false }) => (
    <div className="flex flex-col">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-2 mt-1">
            <p className="font-medium text-gray-700 truncate">{value || "-"}</p>
            {copyable && value && (
                <FaCopy
                    className="text-gray-400 hover:text-primary cursor-pointer flex-shrink-0 text-sm"
                    onClick={() => {
                        navigator.clipboard.writeText(value);
                        toast.success(`${label} copied!`);
                    }}
                />
            )}
        </div>
    </div>
);

const FarmMap = ({ geometry }) => {
    const coords = parseWKTPolygon(geometry);
    if (!coords.length) return null;

    return (
        <MapContainer bounds={coords} className="h-80 w-full rounded-lg" scrollWheelZoom={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Polygon
                positions={coords}
                pathOptions={{ color: "#2FB95D", fillColor: "#2FB95D", fillOpacity: 0.3, weight: 2 }}
            />
        </MapContainer>
    );
};

const CloudCoverChart = ({ cloudCoverData }) => {
    if (!cloudCoverData || cloudCoverData.length === 0) return null;

    return (
        <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={cloudCoverData}>
                <defs>
                    <linearGradient id="cloudGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => val ? String(val).slice(-2) : ''}
                    stroke="#9CA3AF"
                />
                <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `${val}%`}
                    stroke="#9CA3AF"
                />
                <Tooltip
                    formatter={(value) => [`${value}%`, "Cloud Cover"]}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #E5E7EB" }}
                />
                <Area
                    type="monotone"
                    dataKey="cloudCover"
                    stroke="#3B82F6"
                    fill="url(#cloudGradient)"
                    strokeWidth={2}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

const ObservationsYearlyChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    return (
        <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <Tooltip
                    formatter={(value) => [value, "Observations"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #E5E7EB" }}
                />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

const IndicesGrid = ({ methods }) => {
    if (!methods) return null;

    const allIndices = new Set();
    Object.values(methods).forEach((indices) => {
        indices.forEach((idx) => allIndices.add(idx));
    });

    const indexDescriptions = {
        NDVI: "Normalized Difference Vegetation Index - Vegetation health",
        NDWI: "Normalized Difference Water Index - Water content",
        EVI: "Enhanced Vegetation Index - Improved vegetation detection",
        LAI: "Leaf Area Index - Canopy structure",
        NDMI: "Normalized Difference Moisture Index - Moisture stress",
        NDRE: "Normalized Difference Red Edge - Chlorophyll content",
        FAPAR: "Fraction of Absorbed PAR - Light absorption",
        SMI: "Soil Moisture Index - Soil water content",
        CCC: "Canopy Chlorophyll Content",
        CWC: "Canopy Water Content",
        MSAVI2: "Modified Soil-Adjusted Vegetation Index",
        MNDWI: "Modified NDWI - Surface water",
        SOC_VIS: "Soil Organic Carbon (Visible)",
        SOC_SWIR: "Soil Organic Carbon (SWIR)",
        IRECI: "Inverted Red-Edge Chlorophyll Index",
        NDREX: "NDRE Extended",
        NMDI: "Normalized Multi-band Drought Index",
        WIW: "Water In Wetlands",
        RGB: "True Color Image",
    };

    const categoryColors = {
        vegetation: "bg-green-50 border-green-200 text-green-700",
        water: "bg-blue-50 border-blue-200 text-blue-700",
        soil: "bg-amber-50 border-amber-200 text-amber-700",
        other: "bg-purple-50 border-purple-200 text-purple-700",
    };

    const getCategory = (idx) => {
        if (["NDVI", "EVI", "LAI", "NDRE", "FAPAR", "CCC", "MSAVI2", "IRECI", "NDREX"].includes(idx)) return "vegetation";
        if (["NDWI", "NDMI", "CWC", "MNDWI", "NMDI", "WIW", "SMI"].includes(idx)) return "water";
        if (["SOC_VIS", "SOC_SWIR"].includes(idx)) return "soil";
        return "other";
    };

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from(allIndices).sort().map((idx) => (
                <div
                    key={idx}
                    className={`p-3 rounded-lg border ${categoryColors[getCategory(idx)]} cursor-help transition-transform hover:scale-105`}
                    title={indexDescriptions[idx] || idx}
                >
                    <p className="font-semibold text-sm">{idx}</p>
                    <p className="text-xs opacity-75 mt-0.5 truncate">{(indexDescriptions[idx] || "").split(" - ")[1] || ""}</p>
                </div>
            ))}
        </div>
    );
};

const SourceAccordion = ({ source, isFirst }) => {
    const [open, setOpen] = useState(isFirst);

    const firstObs = source.valid_observations?.[0];
    const lastObs = source.valid_observations?.[source.valid_observations.length - 1];
    const totalObs = source.valid_observations?.length || 0;

    const yearlyData = useMemo(() => groupObservationsByYear(source.valid_observations), [source.valid_observations]);
    const cloudCoverData = useMemo(() => processCloudCoverData(source.cloud_cover), [source.cloud_cover]);
    const cloudStats = useMemo(() => calculateCloudStats(source.cloud_cover), [source.cloud_cover]);

    return (
        <div className="border rounded-xl overflow-hidden bg-white">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex justify-between items-center p-5 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 transition-all"
            >
                <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${getStatusDotColor(source.status)}`} />
                    <div className="text-left">
                        <p className="font-bold text-gray-800 text-lg">{source.name}</p>
                        <p className="text-sm text-gray-500">
                            {totalObs.toLocaleString()} observations • {firstObs} to {lastObs}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border ${getStatusColor(source.status)}`}>
                        {source.status}
                    </span>
                    {open ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                </div>
            </button>

            {open && (
                <div className="border-t">
                    {/* Source Metadata */}
                    <div className="p-5 bg-gray-50 border-b">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KeyValue label="Created" value={formatDate(source.created)} />
                            <KeyValue label="Completed" value={formatDate(source.completed)} />
                            <KeyValue label="Last Updated" value={formatDate(source.updated)} />
                            <KeyValue label="Total Observations" value={totalObs.toLocaleString()} />
                        </div>
                        {source.options && Object.keys(source.options).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Processing Options</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(source.options).map(([key, val]) => (
                                        <span key={key} className="px-3 py-1 bg-white border rounded-full text-xs text-gray-600">
                                            {key.replace(/_/g, " ")}: <strong>{String(val)}</strong>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Cloud Cover Analysis (if available) */}
                    {source.cloud_cover && source.cloud_cover.length > 0 && (
                        <div className="p-5 border-b">
                            <div className="flex items-center gap-2 mb-4">
                                <FaCloud className="text-blue-500" />
                                <h4 className="font-semibold text-gray-800">Cloud Cover Analysis</h4>
                            </div>

                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-blue-600">{cloudStats.avg}%</p>
                                    <p className="text-xs text-blue-600">Avg Cloud Cover</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-green-600">{cloudStats.clear}%</p>
                                    <p className="text-xs text-green-600">Clear Days (≤20%)</p>
                                </div>
                                <div className="bg-gray-100 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-gray-600">{cloudStats.cloudy}%</p>
                                    <p className="text-xs text-gray-600">Cloudy Days (&gt;50%)</p>
                                </div>
                            </div>

                            <p className="text-xs text-gray-500 mb-2">Monthly Average Cloud Cover (Last 24 months)</p>
                            <CloudCoverChart cloudCoverData={cloudCoverData} />
                        </div>
                    )}

                    {/* Observations Timeline */}
                    <div className="p-5 border-b">
                        <div className="flex items-center gap-2 mb-4">
                            <FaChartLine className="text-green-500" />
                            <h4 className="font-semibold text-gray-800">Observations by Year</h4>
                        </div>
                        <ObservationsYearlyChart data={yearlyData} />
                    </div>

                    {/* Available Indices */}
                    <div className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <FaThLarge className="text-purple-500" />
                            <h4 className="font-semibold text-gray-800">Available Indices & Methods</h4>
                        </div>

                        <div className="mb-4">
                            <p className="text-xs text-gray-500 mb-2">Analysis Methods Available</p>
                            <div className="flex flex-wrap gap-2">
                                {Object.keys(source.allowed_methods || {}).map((method) => (
                                    <span
                                        key={method}
                                        className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg text-xs font-medium text-blue-700"
                                    >
                                        {method.replaceAll("_", " ")}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 mb-2">Spectral Indices</p>
                        <IndicesGrid methods={source.allowed_methods} />
                    </div>
                </div>
            )}
        </div>
    );
};

/* -------------------- MAIN COMPONENT -------------------- */

const DynacropDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getPolygon(id);
                setData(res);
            } catch (e) {
                console.error(e);
                setError("Failed to load polygon details");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    // Calculate total observations across all sources
    const totalObservations = useMemo(() => {
        if (!data?.sources) return 0;
        return data.sources.reduce((sum, src) => sum + (src.valid_observations?.length || 0), 0);
    }, [data]);

    // Calculate date range
    const dataRange = useMemo(() => {
        if (!data?.sources) return null;
        let earliest = null;
        let latest = null;
        data.sources.forEach((src) => {
            if (src.valid_observations?.length > 0) {
                const first = src.valid_observations[0];
                const last = src.valid_observations[src.valid_observations.length - 1];
                if (!earliest || first < earliest) earliest = first;
                if (!latest || last > latest) latest = last;
            }
        });
        return earliest && latest ? `${earliest} → ${latest}` : null;
    }, [data]);

    if (loading)
        return (
            <div className="h-96 flex flex-col items-center justify-center">
                <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
                <p className="mt-4 text-gray-500">Loading polygon data...</p>
            </div>
        );

    if (error)
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <FaInfoCircle className="text-red-500 text-3xl mx-auto mb-3" />
                    <p className="text-red-700 font-medium">{error}</p>
                    <button
                        onClick={() => navigate("/satellite/dynacrop")}
                        className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    >
                        Back to List
                    </button>
                </div>
            </div>
        );

    return (
        <div>
            <Breadcrumbs
                items={[
                    { name: "Home", link: "/dashboard" },
                    { name: "Satellite", link: "/satellite/list" },
                    { name: "Dynacrop", link: "/satellite/dynacrop" },
                    { name: data.label || "Detail" },
                ]}
            />

            <div className="py-4 space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate("/satellite/dynacrop")}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                        >
                            <FaArrowLeft className="text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Back</span>
                        </button>
                        <div>
                            <StyledHeadingText text={data.label || "Polygon Details"} />
                            <div className="flex items-center gap-1 mt-0.5 text-sm text-gray-500">
                                <span>ID: {data.id?.substring(0, 8)}...</span>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(data.id);
                                        toast.success("Full ID copied!");
                                    }}
                                    className="p-1 hover:text-primary transition-colors"
                                    title="Copy Full ID"
                                >
                                    <FaCopy size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <InsightCard
                        label="Farm Area"
                        value={`${(data.area / 10000).toFixed(2)} ha`}
                        subtext={`${data.area?.toLocaleString()} m²`}
                        icon={FaMapMarkedAlt}
                        color="green-500"
                    />
                    <InsightCard
                        label="Active Plan"
                        value={data.plan?.name || "-"}
                        subtext={`${data.plan?.start} → ${data.plan?.end}`}
                        icon={FaCalendarAlt}
                        color="blue-500"
                    />
                    <InsightCard
                        label="Satellite Sources"
                        value={data.sources?.length || 0}
                        subtext={data.sources?.map(s => s.name).join(", ")}
                        icon={FaSatellite}
                        color="purple-500"
                    />
                    <InsightCard
                        label="Total Observations"
                        value={totalObservations.toLocaleString()}
                        subtext={dataRange}
                        icon={FaChartLine}
                        color="amber-500"
                    />
                </div>

                {/* Map Section */}
                <div className="bg-white border rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 p-4 border-b bg-gray-50">
                        <FaMapMarkedAlt className="text-green-500" />
                        <h3 className="font-semibold text-gray-800">Farm Location</h3>
                    </div>
                    <FarmMap geometry={data.geometry} />
                    <div className="p-4 bg-gray-50 border-t">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <p className="text-xs text-gray-500">WKT Geometry</p>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(data.geometry);
                                    toast.success("Geometry copied!");
                                }}
                                className="flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                                <FaCopy /> Copy Geometry
                            </button>
                        </div>
                        <code className="text-xs text-gray-600 mt-2 block truncate">{data.geometry}</code>
                    </div>
                </div>

                {/* Satellite Sources */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <FaSatellite className="text-purple-500 text-lg" />
                        <h3 className="font-bold text-xl text-gray-800">Satellite Data Sources</h3>
                    </div>
                    {data.sources?.map((src, i) => (
                        <SourceAccordion key={i} source={src} isFirst={i === 0} />
                    ))}
                </div>

                {/* Info Summary */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <FaInfoCircle className="text-green-500" />
                        What you can do with this data
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Monitor vegetation health over time using NDVI, EVI, LAI
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Detect water stress and moisture levels with NDWI, NDMI
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Create management zones for variable rate application
                            </li>
                        </ul>
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">✓</span>
                                Analyze soil moisture patterns with Sentinel-1 radar
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">✓</span>
                                Track seasonal trends across multiple years
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">✓</span>
                                Generate field zonation maps for precision agriculture
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Metadata Footer */}
                <div className="bg-gray-50 rounded-xl p-4 border">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <KeyValue label="Polygon ID" value={data.id} copyable />
                        <KeyValue label="Created" value={formatDate(data.created)} />
                        <KeyValue label="Plan Start" value={data.plan?.start} />
                        <KeyValue label="Plan End" value={data.plan?.end} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DynacropDetail;
